import type GeoJSON from 'geojson';
import { Html, OrbitControls } from '@react-three/drei';
import { Canvas, ThreeEvent, useFrame } from '@react-three/fiber';
import { resourcesAtom } from 'atoms/root';
import classNames from 'classnames';
import {
  Button,
  Checkbox,
  Dropdown,
  InlineLoading,
  InlineNotification,
  Modal,
  TextInput,
} from '@carbon/react';
import {
  CaretDown,
  CaretRight,
  CollapseAll,
  Edit,
  ExpandAll,
  Renew,
} from '@carbon/react/icons';
import { SelectWorkspacePanel } from 'components/pre-drill-workspace-launcher';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import Compass from 'components/compass/compass';
import { Grid3D } from 'components/grid3d/grid-3d';
import StructureGridCollectionsList from 'components/structure-grid-collections-list/structure-grid-collections-list';
import SubsurfaceStructureGridCollections from 'components/structure-grid-collections-list/subsurface-structure-grid-collections';
import WellSurveyAndStructureGridEmptyState from 'components/well-survey-structure-grid-empty-state/well-survey-structure-grid-empty-state';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { throttle } from 'lodash-es';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSliderContext } from 'components/well-page-content/subsurface-explorer/slider-context';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Grid, StructureGridCollection } from 'types/api-responses';
import { format0 } from '../../utils/number';
import { parseUTCdateTime } from 'utils/dates';
import { Toggle } from 'components/toggle/toggle';
import {
  paddedBoundingBoxGeometryAtom,
  structureGridCollectionsAtom,
  wellDeviationSurveyMutationAtom,
  wellSearchEnvelopeAtom,
} from './atoms';
import { colorByAttributeAtom } from 'components/well-page-content/atoms';
import { StructureGridSurfaces } from './structure-grid-surfaces';
import {
  COLOR_BY_OPTIONS,
  computeZTickLength,
  isBoreVisible,
  isDefaultVisibleBore,
  TrajectoryPoint,
  transformWellData,
  WellData,
} from './utils';
import { VerticalExaggerationSlider } from './vertical-exaggeration-slider';
import { SubjectReferenceSliderFromContext } from './subject-reference-slider';
import styles from './well-3d-viewer.module.scss';
import WellSelectorMap, { type InsetScopedLayers } from './well-selector-map';

const Well3D = ({
  points,
  color,
  uwi,
  type,
  estimated = false,
  colorBy,
  currentOperator,
  status,
  landingZone,
  isModal = false,
}: {
  points: TrajectoryPoint[];
  color: string;
  uwi: string;
  type: string;
  /** True for an estimated (synthesized vertical) bore — drives the dashed +
   *  thinner/semi-transparent styling, independently of `type`. */
  estimated?: boolean;
  colorBy: 'well_status' | 'operator' | 'landing_zone';
  currentOperator?: string;
  status?: string;
  landingZone?: string;
  isModal?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  const [hoverPosition, setHoverPosition] = useState<THREE.Vector3 | null>(
    null
  );
  const groupRef = useRef<THREE.Group | null>(null);

  useEffect(() => {
    return () => {
      setHovered(false);
      setHoverPosition(null);
    };
  }, [uwi]);

  const positions = points.map(
    (point) => new THREE.Vector3(point.x, point.y, point.z)
  );

  const linePoints = positions.flatMap((point) => [point.x, point.y, point.z]);

  const lineGeometry = useMemo(() => {
    const geometry = new LineGeometry();
    geometry.setPositions(linePoints);
    return geometry;
  }, [linePoints]);

  const lineMaterial = useMemo(() => {
    // Sidetracks and pre-drill geometry render dashed. Estimated (synthesized) geometry
    // renders dashed AND thinner + semi-transparent, so it reads as a lower-confidence
    // estimate rather than a surveyed or planned bore. Detected via the `estimated`
    // flag — `type` carries the bore-class (e.g. 'producing') even for an estimated bore.
    const isDashed = type === 'sidetrack' || type === 'pre_drill' || estimated;
    return new LineMaterial({
      color: new THREE.Color(color).getHex(),
      linewidth: estimated ? 3 : 5,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      dashed: isDashed,
      dashSize: isDashed ? 0.2 : 1,
      gapSize: isDashed ? 0.1 : 0,
      transparent: estimated,
      opacity: estimated ? 0.55 : 1,
      toneMapped: false,
    });
  }, [color, type, estimated]);

  const line = useMemo(() => {
    const lineObj = new Line2(lineGeometry, lineMaterial);
    // Dashed lines require per-vertex distances to render their gaps.
    if (type === 'sidetrack' || type === 'pre_drill' || estimated) {
      lineObj.computeLineDistances();
    }
    return lineObj;
  }, [lineGeometry, lineMaterial, type, estimated]);

  const curve = useMemo(
    () => new THREE.CatmullRomCurve3(positions),
    [positions]
  );

  // Type-first, matching the wellbore panel: the hover label is the bore's
  // classification, so an estimated bore reads its type (e.g. "Sidetrack"),
  // not the provenance word. `estimated` is only the fallback for an estimated
  // bore with no classified type.
  const wellTypeLabel =
    type === 'producing'
      ? 'Producing Well'
      : type === 'pre_drill'
        ? 'Pre-Drill (estimated)'
        : type === 'sidetrack'
          ? 'Sidetrack'
          : estimated
            ? 'Estimated'
            : 'Sidetrack';

  const getTooltipSubtitle = () => {
    switch (colorBy) {
      case 'operator':
        return `Operator: ${currentOperator || 'N/A'}`;
      case 'well_status':
        return `Well Status: ${status || 'N/A'}`;
      case 'landing_zone':
        return isModal ? wellTypeLabel : `Landing Zone: ${landingZone || '--'}`;
      default:
        return wellTypeLabel;
    }
  };

  // Early return after all hooks
  if (points.length === 0) return null;

  // event.point is in world coordinates. Convert to the Well3D group's
  // local space using Three.js's worldToLocal so the tooltip position
  // correctly accounts for all parent transforms (including VE scaling).
  const toLocalPosition = (worldPoint: THREE.Vector3): THREE.Vector3 => {
    if (!groupRef.current) return worldPoint.clone();
    return groupRef.current.worldToLocal(worldPoint.clone());
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    setHovered(true);
    if (event.point) setHoverPosition(toLocalPosition(event.point));
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    setHovered(false);
    setHoverPosition(null);
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (hovered && event.point) {
      setHoverPosition(toLocalPosition(event.point));
    }
  };

  return (
    <group
      ref={(node) => {
        groupRef.current = node;
      }}
    >
      <primitive object={line} />
      <mesh
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerMove={handlePointerMove}
      >
        <tubeGeometry args={[curve, 64, 30, 8, false]} />
        <meshBasicMaterial transparent opacity={0} visible={false} />
      </mesh>

      {hovered && hoverPosition && (
        <Html position={hoverPosition} className={styles.wellTooltip}>
          <div className={styles.tooltipContent}>
            <strong>{uwi}</strong>
            <br />
            <small>{getTooltipSubtitle()}</small>
          </div>
        </Html>
      )}
    </group>
  );
};
const CollapsibleSection = ({
  title,
  children,
  defaultExpanded = true,
  expanded,
}: {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(
    expanded !== undefined ? expanded : defaultExpanded
  );

  // Sync internal state when the external `expanded` prop changes (e.g. tab switch),
  // but then hand control back to the user so they can toggle freely.
  useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const handleToggle = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  return (
    <div className={styles.controlSection}>
      <Accordion
        expanded={isExpanded}
        onClick={handleToggle}
        className={styles.collapsibleAccordion}
      >
        <AccordionSummary className={styles.collapsibleSummary}>
          {title}
        </AccordionSummary>
        <AccordionDetails className={styles.collapsibleDetails}>
          {children}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

interface ControlsPanelProps {
  /**
   * Fill the width of whatever holds the panel (a drawer, a rail) instead of the fixed
   * 400/450px it takes beside the viewer, and drop the right border. Set here rather
   * than by a host's `className`, whose rule would tie with the panel's own widths and
   * lose whenever this stylesheet loads after the host's.
   */
  fill?: boolean;
  /**
   * True when the host, not the panel, is the thing that scrolls — a rail. The panel
   * then takes its content's height (`height: auto`, `overflow: visible`) and the rail
   * scrolls it all, so the inset map has to yield a plain wheel to that scroll
   * (Mapbox's cooperative gestures: Ctrl/⌘ + wheel zooms the map, a bare wheel scrolls
   * the rail). Beside the viewer or in the drawer the panel scrolls itself under a fixed
   * map, so the map keeps wheel-zoom as it always has.
   */
  hostScrolls?: boolean;
  wells: WellData[];
  selectedWellBores: string[];
  onWellBoreToggle: (wellBoreId: string) => void;
  onWellToggle: (wellUwi: string) => void;
  showSidetracks: boolean;
  onSidetrackToggle: (show: boolean) => void;
  // Gates pre-drill (permit) bore visibility. Toggle hidden when no handler.
  showPermits?: boolean;
  onPermitsToggle?: (show: boolean) => void;
  showVerticalWells?: boolean;
  onVerticalWellsToggle?: (show: boolean) => void;
  showUncorrectedGeometries?: boolean;
  onUncorrectedGeometriesToggle?: (show: boolean) => void;
  showTypeCurveFormations?: boolean;
  onTypeCurveFormationsToggle?: (show: boolean) => void;
  coordinates?: { latitude: number; longitude: number; uwi?: string };
  appliedRadius: string;
  onRadiusChange: (value: string) => void;
  wellDeviationSurveyState: { loading: boolean };
  structureGridCollectionsLoading?: boolean;
  structureGridCollections: StructureGridCollection[];
  individualGrids: Grid[];
  onStructureGridCheckedChange: (checkedIds: number[]) => void;
  selectedStructureGridIds: number[];
  structureGridSelectionMode?: 'checkbox' | 'radio';
  showSelectAll?: boolean;
  onSelectAll?: (selected: boolean) => void;
  /** Restores the wellbore checkbox selection to the page-load default. */
  onResetWellBores?: () => void;
  useGeometryLabel?: boolean;
  disableUncorrectedGeometries?: boolean;
  disableTypeCurveFormations?: boolean;
  selectedCollectionId?: number | null;
  onCollectionChange?: (collectionId: number) => void;
  sourceUwi?: string;
  disableNonSourceWells?: boolean;
  sourceWellBlack?: boolean;
  wellTrajectoriesExpanded?: boolean;
  displaySettingsExpanded?: boolean;
  landingAnalysisMode?: boolean;
  minRadius?: number;
  maxRadius?: number;
  radiusHelperText?: string;
  showLandingAnalysis?: boolean;
  showDirectionTriangleLegend?: boolean;
  /** When false (GunBarrel/Well Spacing mode), hides the radius circle on the map and the radius input controls */
  showSearchArea?: boolean;
  /** When false, hides the gun barrel slice rectangle on the map */
  showSlice?: boolean;
  /** Server-stamped MVT templates for the inset scoped layers (INS-8658). */
  scopedTileUrls?: { nearbyWells: string; offsetWells?: string };
  /** Subject + Intersecting-Units data, reused from the well page (INS-8658 follow-up). */
  insetScopedLayers?: InsetScopedLayers;
  /** Fired after a pre-drill workspace is applied via a permit bore's edit
   *  pencil; the caller re-fetches the wells survey to drive 2D/3D/tables. */
  onWorkspaceApplied?: (uwi14: string) => void;
}

// Wraps WellSelectorMap and the slice-related indicator lines so they can
// subscribe to slider context independently of ControlsPanel. Slider commits
// re-render this component (and only this component within the inset map
// section) — the parent ControlsPanel skips its render entirely thanks to memo.
const InsetMapWithSliceFromContext = ({
  coordinates,
  radiusInFeet,
  searchEnvelopePolygon,
  sceneExtentPolygon,
  showCircle,
  showSlice,
  landingAnalysisMode,
  showDirectionTriangleLegend,
  scopedTileUrls,
  insetScopedLayers,
  cooperativeGestures,
}: {
  coordinates?: { latitude: number; longitude: number; uwi?: string };
  radiusInFeet?: number;
  searchEnvelopePolygon?: GeoJSON.Polygon;
  sceneExtentPolygon?: GeoJSON.Polygon;
  showCircle: boolean;
  showSlice: boolean;
  landingAnalysisMode: boolean;
  showDirectionTriangleLegend: boolean;
  scopedTileUrls?: { nearbyWells: string; offsetWells?: string };
  insetScopedLayers?: InsetScopedLayers;
  cooperativeGestures?: boolean;
}) => {
  const slider = useSliderContext();
  return (
    <>
      <WellSelectorMap
        coordinates={coordinates}
        radiusInFeet={radiusInFeet}
        searchEnvelopePolygon={searchEnvelopePolygon}
        sceneExtentPolygon={sceneExtentPolygon}
        activeSliceCoords={slider?.activeEvalPointCoords}
        wellAzimuthDegree={slider?.subjectWellAzimuth}
        sliceHalfWidthFt={slider?.sliceHalfWidthFt}
        sliceFlipped={slider?.sliceFlipped ?? false}
        highlightedSurvey={slider?.highlightedSurvey ?? null}
        highlightedSurveyStatus={slider?.highlightedSurveyStatus ?? null}
        highlightedUwi={slider?.highlightedUwi ?? null}
        showCircle={showCircle}
        showSlice={showSlice}
        landingAnalysisMode={landingAnalysisMode}
        subjectUwi={coordinates?.uwi}
        nearbyTilesUrl={scopedTileUrls?.nearbyWells ?? null}
        offsetTilesUrl={scopedTileUrls?.offsetWells ?? null}
        insetScopedLayers={insetScopedLayers}
        cooperativeGestures={cooperativeGestures}
      />
      <div className={styles.indicatorLinesContainer}>
        <div className={styles.indicatorLine}>
          <div className={styles.indicatorLineBlack}></div>
          <span className={styles.indicatorLabel}>Structure Grid Extents</span>
        </div>
        {showCircle && (
          <div className={styles.indicatorLine}>
            <div className={styles.indicatorLinePink}></div>
            <span className={styles.indicatorLabel}>Search Area</span>
          </div>
        )}
        {/* Same conditions the slice layer itself draws under — a vertical
            subject has slice coords from its surface location before any azimuth
            is chosen, and advertising a line that is not on the map is worse than
            no legend. */}
        {showSlice &&
          slider?.activeEvalPointCoords &&
          slider?.subjectWellAzimuth !== undefined && (
            <div className={styles.indicatorLine}>
              <div className={styles.indicatorLinePink}></div>
              <span className={styles.indicatorLabel}>Gun Barrel Slice</span>
            </div>
          )}
        {showDirectionTriangleLegend && (
          <div className={styles.indicatorLine}>
            <div
              className={styles.indicatorTriangle}
              style={
                slider?.sliceFlipped ? { transform: 'scaleX(-1)' } : undefined
              }
            />
            <span className={styles.indicatorLabel}>Viewing Direction</span>
          </div>
        )}
      </div>
    </>
  );
};

/** Left sidebar: radius, wells, color-by, structure grids. Exported as memoized ControlsPanel.
 * @return {JSX.Element} Subsurface controls panel UI
 */
const ViewerControlsSidebar = ({
  wells,
  selectedWellBores,
  onWellBoreToggle,
  onWellToggle,
  showSidetracks,
  onSidetrackToggle,
  showPermits,
  onPermitsToggle,
  showVerticalWells,
  onVerticalWellsToggle,
  showUncorrectedGeometries,
  onUncorrectedGeometriesToggle,
  showTypeCurveFormations,
  onTypeCurveFormationsToggle,
  coordinates,
  appliedRadius,
  onRadiusChange,
  wellDeviationSurveyState,
  structureGridCollectionsLoading = false,
  structureGridCollections,
  individualGrids,
  onStructureGridCheckedChange,
  selectedStructureGridIds,
  structureGridSelectionMode = 'checkbox',
  showSelectAll = false,
  onSelectAll,
  onResetWellBores,
  useGeometryLabel = false,
  disableUncorrectedGeometries = false,
  disableTypeCurveFormations = false,
  selectedCollectionId,
  onCollectionChange,
  sourceUwi,
  disableNonSourceWells = false,
  sourceWellBlack = false,
  wellTrajectoriesExpanded = true,
  displaySettingsExpanded = true,
  landingAnalysisMode = false,
  minRadius = 5280,
  maxRadius = 21120,
  radiusHelperText,
  showLandingAnalysis = false,
  showDirectionTriangleLegend = false,
  showSearchArea = true,
  showSlice = true,
  scopedTileUrls,
  insetScopedLayers,
  onWorkspaceApplied,
  fill = false,
  hostScrolls = false,
}: ControlsPanelProps) => {
  const [colorBy, setColorBy] = useAtom(colorByAttributeAtom);
  // Permit bore whose "Select Workspace" edit panel is open (uwi14 + the
  // currently-driving workspace href, to pre-select the panel).
  const [editWorkspace, setEditWorkspace] = useState<{
    uwi14: string;
    currentHref?: string;
  } | null>(null);
  const paddedBoundingBoxGeometry = useAtomValue(paddedBoundingBoxGeometryAtom);
  const wellSearchEnvelope = useAtomValue(wellSearchEnvelopeAtom);
  const [radiusValue, setRadiusValue] = useState(appliedRadius || '10,560');
  const [radiusError, setRadiusError] = useState('');
  const [expandedWells, setExpandedWells] = useState<Record<string, boolean>>(
    () => Object.fromEntries(wells.map((w) => [w.uwi, true]))
  );

  useEffect(() => {
    if (appliedRadius) setRadiusValue(appliedRadius);
  }, [appliedRadius]);

  // Local radius validation using configurable min/max
  const validateRadiusLocal = (value: string): boolean => {
    if (!value) return false;
    const numericValue = parseInt(value.replace(/,/g, ''), 10);
    return (
      !isNaN(numericValue) &&
      numericValue >= minRadius &&
      numericValue <= maxRadius
    );
  };

  const formatRadiusValue = (value: number): string => {
    return format0(value);
  };

  const defaultRadiusHelperText = `Enter Values Between ${formatRadiusValue(
    minRadius
  )} ft and ${formatRadiusValue(maxRadius)} ft`;

  const handleApplyRadius = () => {
    if (validateRadiusLocal(radiusValue)) {
      onRadiusChange(radiusValue);
      setRadiusError('');
    } else {
      setRadiusError(
        `Value must be between ${formatRadiusValue(
          minRadius
        )} ft and ${formatRadiusValue(maxRadius)} ft`
      );
    }
  };

  const handleRadiusInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    // Format with commas for display
    const numValue = parseInt(value.replace(/,/g, ''), 10);
    if (!isNaN(numValue)) {
      const formattedValue = format0(numValue);
      setRadiusValue(formattedValue);
    } else {
      setRadiusValue(value);
    }
    if (radiusError) setRadiusError('');
  };

  const isRadiusValid = validateRadiusLocal(radiusValue);

  // A well's checkbox reflects only its ACTIVE bores (those not hidden by a
  // toggle) — hidden bores are greyed/non-selectable, so they don't count.
  const activeBores = (well: WellData) =>
    well.wellBores.filter((bore) =>
      isBoreVisible(bore, showSidetracks, showPermits ?? true)
    );

  const isWellFullySelected = (well: WellData): boolean => {
    const bores = activeBores(well);
    return (
      bores.length > 0 &&
      bores.every((bore) => selectedWellBores.includes(bore.id))
    );
  };

  const isWellPartiallySelected = (well: WellData): boolean =>
    activeBores(well).some((bore) => selectedWellBores.includes(bore.id)) &&
    !isWellFullySelected(well);

  // Active wellbore ids across all wells (for the Select All checkbox state).
  const allVisibleWellBoreIds = useMemo(
    () => wells.flatMap((well) => activeBores(well).map((bore) => bore.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wells, showSidetracks, showPermits]
  );

  const isAllSelected = useMemo(() => {
    if (allVisibleWellBoreIds.length === 0) return false;
    return allVisibleWellBoreIds.every((id) => selectedWellBores.includes(id));
  }, [allVisibleWellBoreIds, selectedWellBores]);

  const handleSelectAll = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    if (onSelectAll) {
      onSelectAll(checked);
    }
  };

  return (
    <>
      <div
        className={classNames(styles.controlsPanel, {
          [styles.controlsPanelFill]: fill,
          [styles.controlsPanelHostScrolls]: hostScrolls,
        })}
      >
        <div className={styles.insetMapFixed}>
          <InsetMapWithSliceFromContext
            coordinates={coordinates}
            radiusInFeet={
              appliedRadius
                ? parseInt(appliedRadius.replace(/,/g, ''), 10)
                : undefined
            }
            searchEnvelopePolygon={wellSearchEnvelope ?? undefined}
            sceneExtentPolygon={paddedBoundingBoxGeometry ?? undefined}
            showCircle={showSearchArea}
            showSlice={showSlice}
            landingAnalysisMode={landingAnalysisMode}
            showDirectionTriangleLegend={showDirectionTriangleLegend}
            scopedTileUrls={scopedTileUrls}
            insetScopedLayers={insetScopedLayers}
            cooperativeGestures={hostScrolls}
          />
        </div>
        <div className={styles.controlsPanelScroll}>
          <CollapsibleSection
            title="Display Settings"
            defaultExpanded={displaySettingsExpanded}
          >
            <div className={styles.mapRadiusContent}>
              <>
                <div className={styles.radiusInputContainer}>
                  <TextInput
                    id="radius-input"
                    labelText="Well Search Radius (ft)"
                    value={radiusValue}
                    onChange={handleRadiusInputChange}
                    placeholder=""
                    className={styles.radiusTextInput}
                    helperText={
                      radiusError || radiusHelperText || defaultRadiusHelperText
                    }
                    invalid={!!radiusError}
                  />
                </div>
                <div className={styles.applyButtonContainer}>
                  <Button
                    onClick={handleApplyRadius}
                    disabled={!isRadiusValid}
                    size="sm"
                  >
                    Update
                  </Button>
                </div>
              </>
              {showLandingAnalysis && <SubjectReferenceSliderFromContext />}
              <div className={styles.insetMapDropdowns}>
                <div
                  className={styles.colorByDropdownContainer}
                  data-disabled={wellDeviationSurveyState.loading || undefined}
                >
                  <Dropdown
                    id="color-by-attribute"
                    titleText="Color Nearby Wells By"
                    label=""
                    items={COLOR_BY_OPTIONS}
                    itemToString={(item) => item?.label ?? ''}
                    selectedItem={
                      COLOR_BY_OPTIONS.find((opt) => opt.id === colorBy) ??
                      COLOR_BY_OPTIONS[0]
                    }
                    disabled={wellDeviationSurveyState.loading}
                    onChange={({
                      selectedItem,
                    }: {
                      selectedItem: (typeof COLOR_BY_OPTIONS)[number] | null;
                    }) => {
                      if (selectedItem) setColorBy(selectedItem.id);
                    }}
                  />
                  {colorBy === 'landing_zone' && (
                    <p className={styles.colorByHelperText}>
                      Landing Zone coloring based on current Landing Results
                      Summary table
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Wells"
            defaultExpanded={wellTrajectoriesExpanded}
          >
            {wellDeviationSurveyState.loading ? (
              <div className={styles.emptyMessage}>
                <InlineLoading description="Loading Well Surveys..." />
              </div>
            ) : wells.length === 0 ? (
              <div className={styles.emptyMessage}>
                No Well Surveys Available
              </div>
            ) : (
              <>
                <div className={styles.sidetrackToggleContainer}>
                  {onPermitsToggle && (
                    <Toggle
                      isActive={showPermits ?? true}
                      handleToggle={onPermitsToggle}
                      size="small"
                      label="Show Permits"
                      className={styles.panelToggle}
                    />
                  )}
                  {onVerticalWellsToggle && (
                    <Toggle
                      isActive={showVerticalWells ?? true}
                      handleToggle={onVerticalWellsToggle}
                      size="small"
                      label="Show Vertical Wells"
                      className={styles.panelToggle}
                    />
                  )}
                  <Toggle
                    isActive={showSidetracks}
                    handleToggle={onSidetrackToggle}
                    size="small"
                    label="Show Sidetracks"
                    className={styles.panelToggle}
                  />
                  {onUncorrectedGeometriesToggle && (
                    <Toggle
                      isActive={showUncorrectedGeometries ?? false}
                      handleToggle={onUncorrectedGeometriesToggle}
                      size="small"
                      label="Show Uncorrected Wellbore Geometries"
                      disabled={
                        disableUncorrectedGeometries || landingAnalysisMode
                      }
                      className={styles.panelToggle}
                    />
                  )}
                </div>

                <div className={styles.wellboresSection}>
                  <div className={styles.wellboresSectionHeader}>
                    <span className={styles.wellboresSectionTitle}>
                      Wellbores
                    </span>
                    <span className={styles.wellboresSectionActions}>
                      <Button
                        kind="ghost"
                        size="sm"
                        hasIconOnly
                        renderIcon={CollapseAll}
                        iconDescription="Collapse all"
                        onClick={() => setExpandedWells({})}
                      />
                      <Button
                        kind="ghost"
                        size="sm"
                        hasIconOnly
                        renderIcon={ExpandAll}
                        iconDescription="Expand all"
                        onClick={() =>
                          setExpandedWells(
                            Object.fromEntries(wells.map((w) => [w.uwi, true]))
                          )
                        }
                      />
                      {onResetWellBores && (
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={Renew}
                          iconDescription="Reset to latest well bores"
                          onClick={onResetWellBores}
                        />
                      )}
                      {showSelectAll && (
                        <Checkbox
                          id="select-all-wells"
                          labelText=""
                          hideLabel
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                      )}
                    </span>
                  </div>

                  {wells.map((well, wellIndex) => {
                    const isSourceWell = sourceUwi
                      ? well.uwi === sourceUwi
                      : false;
                    const displayColor =
                      sourceWellBlack && isSourceWell ? '#000000' : well.color;
                    const isFullySelected = isWellFullySelected(well);
                    const isPartiallySelected = isWellPartiallySelected(well);
                    const wellKey = `${well.uwi}-${wellIndex}`;
                    const isWellDisabled =
                      disableNonSourceWells && !isSourceWell;
                    const isExpanded = !!expandedWells[well.uwi];
                    const wellDisplayName =
                      well.name ||
                      (sourceUwi && isSourceWell
                        ? `${well.uwi} [Subject Well]`
                        : well.uwi);

                    return (
                      <div key={wellKey} className={styles.wellboreWellItem}>
                        <div className={styles.wellboreWellHeaderRow}>
                          <span className={styles.wellboreWellHeaderLeft}>
                            <span
                              className={styles.wellboreCaret}
                              role="button"
                              onClick={() =>
                                setExpandedWells((prev) => ({
                                  ...prev,
                                  [well.uwi]: !prev[well.uwi],
                                }))
                              }
                            >
                              {isExpanded ? <CaretDown /> : <CaretRight />}
                            </span>
                            <span className={styles.wellboreName}>
                              {wellDisplayName}
                            </span>
                          </span>
                          <span className={styles.wellboreWellHeaderRight}>
                            <Checkbox
                              id={`well-${wellKey}`}
                              labelText=""
                              hideLabel
                              checked={isFullySelected}
                              indeterminate={isPartiallySelected}
                              onChange={() => onWellToggle(well.uwi)}
                              disabled={isWellDisabled}
                            />
                          </span>
                        </div>
                        {isExpanded && (
                          <div className={styles.wellboreBoreList}>
                            {well.wellBores.map((wellBore) => {
                              // Key on the unique bore id, not uwi14 — a drilled
                              // bore and a pre-drill can share a uwi14.
                              const wellBoreKey = wellBore.id;
                              // Bores hidden by a toggle (Show Sidetracks / Show
                              // Permits) stay listed but greyed + non-selectable,
                              // and are excluded from the plots.
                              const isBoreActive = isBoreVisible(
                                wellBore,
                                showSidetracks,
                                showPermits ?? true
                              );
                              // Pre-drill bores are surfaced as "Permit" to
                              // match the landing-analysis table and Save modal.
                              // Estimated bores fall through to their geometry
                              // label — the classified bore type (Producing /
                              // Sidetrack), same as a drilled bore.
                              // Provenance is conveyed by the swatch + line style
                              // (opacity / width), not this label.
                              const boreLabel =
                                wellBore.type === 'pre_drill'
                                  ? 'Permit'
                                  : useGeometryLabel && wellBore.geometryLabel
                                    ? wellBore.geometryLabel
                                    : wellBore.type;
                              const formattedSpudDate = wellBore.spudDate
                                ? parseUTCdateTime(wellBore.spudDate)
                                : '--';
                              return (
                                <div
                                  key={wellBoreKey}
                                  className={styles.wellboreBoreItem}
                                  data-disabled={!isBoreActive || undefined}
                                >
                                  <span
                                    className={styles.wellboreColorSwatch}
                                    style={{ background: displayColor }}
                                  />
                                  <span className={styles.wellboreBoreInfo}>
                                    <span className={styles.wellboreBoreUwi}>
                                      {wellBore.uwi14}
                                    </span>
                                    <span className={styles.wellboreBoreMeta}>
                                      Spud Date: {formattedSpudDate} |{' '}
                                      {boreLabel}
                                    </span>
                                    {wellBore.type === 'pre_drill' && (
                                      <span
                                        className={styles.wellboreWorkspaceLine}
                                      >
                                        Workspace:{' '}
                                        <span
                                          className={
                                            styles.wellboreWorkspaceName
                                          }
                                        >
                                          {wellBore.workspace?.name ?? '--'}
                                        </span>
                                        <Button
                                          hasIconOnly
                                          renderIcon={Edit}
                                          kind="ghost"
                                          size="sm"
                                          iconDescription="Select workspace"
                                          tooltipPosition="top"
                                          onClick={() =>
                                            setEditWorkspace({
                                              uwi14: wellBore.uwi14,
                                              currentHref:
                                                wellBore.workspace?.href,
                                            })
                                          }
                                        />
                                      </span>
                                    )}
                                  </span>
                                  <span className={styles.wellboreBoreCheckbox}>
                                    <Checkbox
                                      id={`wellbore-${wellBoreKey}`}
                                      labelText=""
                                      hideLabel
                                      checked={selectedWellBores.includes(
                                        wellBore.id
                                      )}
                                      onChange={() =>
                                        onWellBoreToggle(wellBore.id)
                                      }
                                      disabled={isWellDisabled || !isBoreActive}
                                    />
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!showLandingAnalysis && (
                  <div className={styles.controlSection}>
                    <h3>Legend</h3>
                    <div className={styles.legendItem}>
                      <span className={styles.legendSolidLine}></span>
                      <span>Producing Well (Solid)</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={styles.legendDashedLine}></span>
                      <span>Sidetrack (Dashed)</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={styles.legendDashedLine}></span>
                      <span>Pre-Drill (Estimated, Dashed)</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title="Structure Grid Collections"
            defaultExpanded={true}
            expanded={landingAnalysisMode ? false : undefined}
          >
            {onTypeCurveFormationsToggle && (
              <div className={styles.typeCurveToggleContainer}>
                <Toggle
                  isActive={showTypeCurveFormations ?? false}
                  handleToggle={onTypeCurveFormationsToggle}
                  size="small"
                  label="Show Type Curve Formations"
                  disabled={disableTypeCurveFormations || landingAnalysisMode}
                  className={styles.panelToggle}
                />
              </div>
            )}
            {structureGridCollectionsLoading ? (
              <div className={styles.emptyMessage}>
                <InlineLoading description="Loading Structure Grid Collections..." />
              </div>
            ) : structureGridSelectionMode === 'radio' ? (
              <SubsurfaceStructureGridCollections
                collections={structureGridCollections}
                individualGrids={individualGrids}
                onSelectionChange={onStructureGridCheckedChange}
                selectedCollectionId={selectedCollectionId}
                onCollectionChange={onCollectionChange}
                disableIndividualGrids={landingAnalysisMode}
              />
            ) : (
              <StructureGridCollectionsList
                collections={structureGridCollections}
                grids={individualGrids}
                onCheckedChange={onStructureGridCheckedChange}
                selectedGridIds={selectedStructureGridIds}
              />
            )}
          </CollapsibleSection>
        </div>
      </div>
      {editWorkspace && (
        <SelectWorkspacePanel
          open
          uwi14={editWorkspace.uwi14}
          currentWorkspaceHref={editWorkspace.currentHref}
          onClose={() => setEditWorkspace(null)}
          onApplied={(u) => onWorkspaceApplied?.(u)}
        />
      )}
    </>
  );
};

// Memoised so a slider commit's parent re-render doesn't cascade through the
// inset map, wellbore list, dropdowns, and structure-grid panel. Slider state
// is read by InsetMapWithSliceFromContext and SubjectReferenceSliderFromContext via
// SliderContext, so those re-render independently — the rest of ControlsPanel
// stays cached.
const ControlsPanel = memo(ViewerControlsSidebar);

interface Well3DViewerControlsProps {
  setCompassRotation: (rotation: number) => void;
  target: [number, number, number];
}
const Well3DViewerControls: React.FC<Well3DViewerControlsProps> = ({
  setCompassRotation,
  target,
}) => {
  const controlsRef =
    React.useRef<React.ElementRef<typeof OrbitControls>>(null);
  const throttledUpdate = React.useMemo(
    () =>
      throttle((azimuthal: number) => {
        setCompassRotation(azimuthal);
      }, 100),
    [setCompassRotation]
  );
  useFrame(() => {
    if (controlsRef.current) {
      const azimuthal = controlsRef.current.getAzimuthalAngle();
      throttledUpdate(azimuthal);
    }
  });
  return (
    <OrbitControls
      ref={controlsRef}
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      target={target}
    />
  );
};

// ─── Vertical Exaggeration (linear scale 1×–10×, step 0.1) ───────────────────
const VE_DEFAULT = 5.0;

const Well3DViewer = ({
  isOpen,
  onClose,
  latitude,
  longitude,
  uwi,
}: {
  isOpen: boolean;
  onClose: () => void;
  latitude: number;
  longitude: number;
  uwi?: string;
}) => {
  const [wellData, setWellData] = useState<WellData[]>([]);
  const [structureGridCollections, setStructureGridCollections] = useState<
    StructureGridCollection[]
  >([]);
  const [paddedBoundingBoxGeometry, setPaddedBoundingBoxGeometry] = useAtom(
    paddedBoundingBoxGeometryAtom
  );
  const setWellSearchEnvelope = useSetAtom(wellSearchEnvelopeAtom);
  const [individualGrids, setIndividualGrids] = useState<Grid[]>([]);
  const [selectedStructureGridIds, setSelectedStructureGridIds] = useState<
    number[]
  >([]);

  const [bounds, setBounds] = useState<{
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
  }>({ minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 });

  const [appliedRadius, setAppliedRadius] = useState('10,560');
  const [hasError, setHasError] = useState(false);
  const [selectedWellBores, setSelectedWellBores] = useState<string[]>([]);
  // Snapshot of the selection set on page load, so the header Reset icon can
  // restore it after the user has toggled checkboxes.
  const initialSelectedWellBoresRef = useRef<string[]>([]);
  const handleResetWellBores = useCallback(() => {
    setSelectedWellBores(initialSelectedWellBoresRef.current);
  }, []);
  const [showSidetracks, setShowSidetracks] = useState(true);
  const [tickLength, setTickLength] = useState<{
    xTickLength: number;
    yTickLength: number;
    zTickLength: number;
  }>({ xTickLength: 1000, yTickLength: 1000, zTickLength: 1000 });
  const [compassRotation, setCompassRotation] = useState(Math.PI);
  const [verticalExaggeration, setVerticalExaggeration] = useState(VE_DEFAULT);

  const resources = useAtomValue(resourcesAtom);
  const colorBy = useAtomValue(colorByAttributeAtom);

  const [wellDeviationSurveyState, fetchWellDeviationSurvey] = useAtom(
    wellDeviationSurveyMutationAtom
  );

  const range = {
    x: bounds.maxX - bounds.minX,
    y: bounds.maxY - bounds.minY,
    z: bounds.maxZ - bounds.minZ,
  };

  const center = {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
  };

  const maxRange = Math.max(range.x, range.y, range.z);

  // Create coordinates object from props
  const coordinates =
    latitude && longitude ? { latitude, longitude, uwi } : undefined;

  const [structureGridCollectionsState, fetchStructureGridCollections] =
    useAtom(structureGridCollectionsAtom);

  const fetchWellData = useCallback(() => {
    if (!uwi || !resources?.queries?.wells?.href) {
      return;
    }

    setHasError(false);
    setWellData([]);
    setIndividualGrids([]);
    setWellSearchEnvelope(null);
    setPaddedBoundingBoxGeometry(null);

    fetchWellDeviationSurvey({
      url: resources.queries.wells.href,
      type: 'POST',
      data: {
        source_uwi: uwi,
        radius: appliedRadius.replace(',', ''),
        limit: 9999,
        includeDeviationSurvey3d: true,
        structureGridCollections: true,
        point: `${longitude} ${latitude}`,
        color_by: colorBy,
      },
      onSuccess: (apiData) => {
        const { wells } = transformWellData(apiData);
        setBounds(apiData.paddedBoundingBox);
        setWellData(wells);
        setHasError(false);
        setTickLength({
          xTickLength: apiData.tickRangeDivisions.xTickRange,
          yTickLength: apiData.tickRangeDivisions.yTickRange,
          zTickLength: apiData.tickRangeDivisions.zTickRange,
        });
        if (apiData.wellSearchEnvelopeGeometry) {
          setWellSearchEnvelope(apiData.wellSearchEnvelopeGeometry);
        }
        if (apiData.paddedBoundingBoxGeometry) {
          setPaddedBoundingBoxGeometry(apiData.paddedBoundingBoxGeometry);
          fetchStructureGridCollections({
            url: '/rasters/tiles/structure_grids',
            type: 'POST',
            data: {
              search_geometry: apiData.paddedBoundingBoxGeometry,
            },
            onSuccess: (sgData) => {
              if (sgData && sgData.structureGridCollections) {
                setStructureGridCollections(sgData.structureGridCollections);
              } else {
                setStructureGridCollections([]);
              }

              if (sgData && sgData.grids) {
                setIndividualGrids(sgData.grids as Grid[]);
              } else {
                setIndividualGrids([]);
              }
            },
            onError: (error) => {
              console.error(error);
              setStructureGridCollections([]);
              setIndividualGrids([]);
            },
          });
        }
      },
      onError: () => {
        setWellData([]);
        setHasError(true);
      },
    });
  }, [
    uwi,
    appliedRadius,
    latitude,
    longitude,
    colorBy,
    resources?.queries?.wells?.href,
    fetchWellDeviationSurvey,
    fetchStructureGridCollections,
    setWellSearchEnvelope,
    setPaddedBoundingBoxGeometry,
  ]);

  // Effect to fetch data when modal opens and UWI is available
  useEffect(() => {
    if (isOpen && uwi) {
      setHasError(false);
      setWellData([]);
      setStructureGridCollections([]);
      setIndividualGrids([]);
      fetchWellData();
    }
  }, [isOpen, uwi, appliedRadius, fetchWellData]);

  // Effect to reset selected well bores when wells change
  useEffect(() => {
    const allWellBoreIds = wellData.flatMap((well) =>
      well.wellBores.map((bore) => bore.id)
    );
    initialSelectedWellBoresRef.current = allWellBoreIds;
    setSelectedWellBores(allWellBoreIds);
  }, [wellData]);

  // Effect to clean up when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWellData([]);
      setHasError(false);
      setSelectedStructureGridIds([]);
      setVerticalExaggeration(VE_DEFAULT);
    }
  }, [isOpen]);

  // Effect to update bounds based on structure grid elevations
  useEffect(() => {
    if (
      bounds &&
      (structureGridCollections.length > 0 || individualGrids.length > 0)
    ) {
      let highestElevation = -Infinity;
      let lowestElevation = Infinity;

      // Get the highest and lowest elevations from all structure grids
      structureGridCollections.forEach((collection) => {
        collection.grids.forEach((grid) => {
          if (
            grid.metadata.minElevation !== undefined &&
            grid.metadata.minElevation !== null
          ) {
            const minElev = parseFloat(grid.metadata.minElevation.toString());
            lowestElevation = Math.min(lowestElevation, minElev);
          }
          if (
            grid.metadata.maxElevation !== undefined &&
            grid.metadata.maxElevation !== null
          ) {
            const maxElev = parseFloat(grid.metadata.maxElevation.toString());
            highestElevation = Math.max(highestElevation, maxElev);
          }
        });
      });

      // Get the highest and lowest elevations from individual grids
      individualGrids.forEach((grid) => {
        if (
          grid.metadata.minElevation !== undefined &&
          grid.metadata.minElevation !== null
        ) {
          const minElev = parseFloat(grid.metadata.minElevation.toString());
          lowestElevation = Math.min(lowestElevation, minElev);
        }
        if (
          grid.metadata.maxElevation !== undefined &&
          grid.metadata.maxElevation !== null
        ) {
          const maxElev = parseFloat(grid.metadata.maxElevation.toString());
          highestElevation = Math.max(highestElevation, maxElev);
        }
      });

      // Update bounds with the greater values
      if (highestElevation !== -Infinity || lowestElevation !== Infinity) {
        const newBounds = { ...bounds };

        if (highestElevation !== -Infinity) {
          newBounds.maxZ = Math.max(bounds.maxZ, highestElevation);
        }

        if (lowestElevation !== Infinity) {
          const calculatedMinZ = Math.max(bounds.minZ, lowestElevation);
          newBounds.minZ = Math.max(calculatedMinZ, -30000); // Hard limit of -30000
        }

        setBounds(newBounds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array

  const wellBoresToRender = wellData.flatMap((well) =>
    well.wellBores
      .filter((bore) => {
        const isSelected = selectedWellBores.includes(bore.id);
        const shouldShowSidetrack =
          showSidetracks || isDefaultVisibleBore(bore);
        return isSelected && shouldShowSidetrack;
      })
      .map((bore) => ({
        ...bore,
        color: well.color,
        isSourceWell: well.uwi === uwi,
        currentOperator: well.currentOperator,
        status: well.status,
      }))
  );

  const handleWellBoreToggle = (wellBoreId: string) => {
    setSelectedWellBores((prev) => {
      if (prev.includes(wellBoreId)) {
        return prev.filter((id) => id !== wellBoreId);
      }
      return [...prev, wellBoreId];
    });
  };

  const handleWellToggle = (wellUwi: string) => {
    const well = wellData.find((w) => w.uwi === wellUwi);
    if (!well) return;

    const visibleBores = showSidetracks
      ? well.wellBores
      : well.wellBores.filter((bore) => isDefaultVisibleBore(bore));

    const wellBoreIds = visibleBores.map((bore) => bore.id);
    const allSelected = wellBoreIds.every((id) =>
      selectedWellBores.includes(id)
    );

    setSelectedWellBores((prev) => {
      if (allSelected) {
        return prev.filter((id) => !wellBoreIds.includes(id));
      } else {
        const newIds = wellBoreIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      }
    });
  };

  const handleSidetrackToggle = (show: boolean) => {
    setShowSidetracks(show);
  };

  const handleRadiusChange = (newRadius: string) => {
    setAppliedRadius(newRadius);
  };

  const handleStructureGridCheckedChange = React.useCallback(
    (checkedIds: number[]) => {
      setSelectedStructureGridIds((prev) => {
        if (
          prev.length === checkedIds.length &&
          prev.every((id, i) => id === checkedIds[i])
        ) {
          return prev;
        }
        return checkedIds;
      });
    },
    []
  );

  const shouldRender3DScene =
    !wellDeviationSurveyState.loading &&
    !hasError &&
    (wellData.length > 0 ||
      structureGridCollections.length > 0 ||
      individualGrids.length > 0);

  const noStructureGridsTitle =
    'No Structure Grids are available. Well Surveys are available for viewing.';

  const noWellSurveysTitle =
    'No Well Surveys are available. Structure Grids are available for viewing.';

  if (!isOpen) return null;

  const content = (
    <div className={styles.container}>
      <ControlsPanel
        wells={wellData}
        selectedWellBores={selectedWellBores}
        onWellBoreToggle={handleWellBoreToggle}
        onResetWellBores={handleResetWellBores}
        onWellToggle={handleWellToggle}
        showSidetracks={showSidetracks}
        onSidetrackToggle={handleSidetrackToggle}
        coordinates={coordinates}
        appliedRadius={appliedRadius}
        onRadiusChange={handleRadiusChange}
        wellDeviationSurveyState={wellDeviationSurveyState}
        structureGridCollections={structureGridCollections}
        individualGrids={individualGrids}
        onStructureGridCheckedChange={handleStructureGridCheckedChange}
        selectedStructureGridIds={selectedStructureGridIds}
      />

      <div className={styles.canvasContainer}>
        {wellDeviationSurveyState.loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <InlineLoading description="Loading well deviation survey data..." />
            </div>
          </div>
        )}

        {hasError && !wellDeviationSurveyState.loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <p>Sorry, could not load wells data</p>
            </div>
          </div>
        )}

        {!wellDeviationSurveyState.loading &&
          !structureGridCollectionsState.loading &&
          !hasError &&
          wellData.length === 0 &&
          structureGridCollections.length === 0 &&
          individualGrids.length === 0 && (
            <WellSurveyAndStructureGridEmptyState />
          )}

        {!wellDeviationSurveyState.loading &&
          !structureGridCollectionsState.loading &&
          !hasError &&
          structureGridCollections.length === 0 &&
          individualGrids.length === 0 &&
          wellData.length > 0 && (
            <div className={styles.noStructureGridsOverlay}>
              <InlineNotification
                kind="warning"
                title={noStructureGridsTitle}
              />
            </div>
          )}

        {!wellDeviationSurveyState.loading &&
          !structureGridCollectionsState.loading &&
          !hasError &&
          wellData.length === 0 &&
          structureGridCollections.length > 0 && (
            <div className={styles.noWellSurveysOverlay}>
              <InlineNotification kind="warning" title={noWellSurveysTitle} />
            </div>
          )}

        {shouldRender3DScene && (
          <>
            <div className={styles.compassOverlay}>
              <Compass rotation={compassRotation} />
            </div>
            <Canvas
              camera={{
                position: [
                  // Offset left of center; 0.75× maxRange provides a slight left-angle perspective
                  -center.x - maxRange * 0.75,
                  // Pull camera back in Y; 1.7× maxRange controls zoom level
                  -center.y + maxRange * 1.7,
                  // Match target Z (center.z × VE) so the camera looks straight sideways at the SG
                  center.z * 5,
                ],
                fov: 75,
                far: 1000000,
                near: 0.1,
                aspect: window.innerWidth / window.innerHeight,
                up: [0, 0, 1],
              }}
              gl={{ antialias: true }}
              flat
            >
              <group scale={[-1, -1, 1]}>
                <color attach="background" args={['#f8fafc']} />
                <ambientLight intensity={0.6} />
                <directionalLight
                  position={[20, 20, 20]}
                  intensity={0.8}
                  castShadow
                  shadow-mapSize-width={2048}
                  shadow-mapSize-height={2048}
                />
                <pointLight position={[10, 10, 10]} intensity={0.4} />
                <pointLight position={[-10, -10, -10]} intensity={0.2} />
                {/* VE group — scales only the Z (vertical/elevation) axis */}
                <group scale={[1, 1, verticalExaggeration]}>
                  {wellBoresToRender.map((wellBore) => (
                    <Well3D
                      key={wellBore.id}
                      points={wellBore.trajectory}
                      color={wellBore.color}
                      uwi={wellBore.uwi14}
                      type={wellBore.type}
                      estimated={wellBore.estimated}
                      colorBy={colorBy}
                      currentOperator={wellBore.currentOperator}
                      status={wellBore.status}
                      isModal
                    />
                  ))}
                  <Grid3D
                    origin={{
                      x: bounds.minX,
                      y: bounds.minY,
                      z: bounds.minZ,
                    }}
                    tickLength={{
                      ...tickLength,
                      zTickLength: computeZTickLength(
                        verticalExaggeration,
                        range.z
                      ),
                    }}
                    breadth={range.x}
                    length={range.y}
                    height={range.z}
                  />

                  <StructureGridSurfaces
                    collections={structureGridCollections}
                    individualGrids={individualGrids}
                    selectedGridIds={selectedStructureGridIds}
                    paddedBoundingBoxGeometry={paddedBoundingBoxGeometry}
                  />
                </group>
              </group>
              <Well3DViewerControls
                setCompassRotation={setCompassRotation}
                target={[-center.x, -center.y, center.z * verticalExaggeration]}
              />
            </Canvas>
            <div className={styles.scaleLegend}>
              <p>
                XY Axis Grid Size :{' '}
                <strong>{format0(tickLength?.xTickLength, '0')} ft</strong>
              </p>
            </div>
            <VerticalExaggerationSlider
              value={verticalExaggeration}
              onChange={setVerticalExaggeration}
            />
          </>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading="3D Well Viewer"
      size="lg"
      className={styles.modal}
      passiveModal
    >
      {content}
    </Modal>
  );
};

export default Well3DViewer;
export { CollapsibleSection, ControlsPanel, Well3D, Well3DViewerControls };
